import type { GeoMarkupStore } from '../index';
import { TaskStatus } from '../../types';
import type { Task, TaskListItem } from '../../types';
import { dateToISOString } from '@/utils/dateHelpers';
import { selectHasActiveTaskFilters } from './taskFilterSelectors';
import { effectiveTaskStatuses } from '@/constants/taskStatuses';

// ===== TASK SELECTORS =====

/**
 * Select all tasks from the store (server-filtered, lightweight list items)
 */
export const selectAllTasks = (state: GeoMarkupStore): TaskListItem[] =>
  state.tasks.items;

/**
 * Select the currently selected task (full detail)
 * Returns null if no task is selected or not yet loaded
 */
export const selectSelectedTask = (state: GeoMarkupStore): Task | null =>
  state.tasks.selectedTask;

/**
 * Create a selector for tasks by specific status
 */
export const selectTasksByStatus =
  (status: TaskStatus) =>
  (state: GeoMarkupStore): TaskListItem[] => {
    return state.tasks.getTasksByStatus(status);
  };

/**
 * Select whether task form is open
 */
export const selectIsTaskFormOpen = (state: GeoMarkupStore): boolean => {
  return state.tasks.formIsOpen;
};

/**
 * Select task form mode (create/edit/null)
 */
export const selectTaskFormMode = (
  state: GeoMarkupStore
): 'create' | 'edit' | null => {
  return state.tasks.formMode;
};

/**
 * Select whether task details panel is open
 */
export const selectIsTaskDetailsOpen = (state: GeoMarkupStore): boolean => {
  return state.tasks.detailsIsOpen;
};

/**
 * Select unique locations/roads from all tasks
 */
export const selectUniqueLocations = (state: GeoMarkupStore): string[] => {
  return state.tasks.getUniqueLocations();
};

/**
 * Select completion percentage for a specific task
 */
export const selectTaskCompletionPercentage =
  (taskId: string) =>
  (state: GeoMarkupStore): number => {
    return state.tasks.getCompletionPercentage(taskId);
  };

/**
 * Pagination selectors
 */
export const selectTasksHasNextPage = (state: GeoMarkupStore): boolean =>
  state.tasks.hasNextPage;

/**
 * Total count of tasks (from server pagination metadata)
 */
export const selectTasksTotalCount = (state: GeoMarkupStore): number =>
  state.tasks.totalCount;

/**
 * Whether the paginated task list has settled at least once (success or error).
 * Mirror of `selectNotesHasLoaded`. Distinguishes "still loading" from "empty after fetch".
 */
export const selectTasksHasLoaded = (state: GeoMarkupStore): boolean =>
  state.tasks.hasLoaded;

/**
 * Select map tasks (full Task[] with geometries for map rendering)
 */
export const selectMapTasks = (state: GeoMarkupStore): Task[] =>
  state.tasks.mapTasks;

// ===== FILTERED MAP SELECTORS =====

/**
 * Module-level memoization cache for selectFilteredMapTasks.
 * Only recomputes when mapTasks reference or filter key changes.
 */
let cachedMapTasks: Task[] | null = null;
let cachedTaskFilterKey: string = '';
let cachedFilteredMapTasks: Task[] = [];

/**
 * Select map tasks filtered by the active task filter state.
 * Applies all 7 filter fields (search, status, priority, assignedToId, dateFrom, dateTo, location)
 * client-side to the full mapTasks array.
 *
 * Memoized: only recomputes when mapTasks reference or filter key changes.
 */
export const selectFilteredMapTasks = (state: GeoMarkupStore): Task[] => {
  const mapTasks = state.tasks.mapTasks;
  const { search, status, priority, assignedToId, dateFrom, dateTo, location } =
    state.taskFilters;

  // When no status is explicitly selected, fall back to the default-visible set
  // (excludes Archived), mirroring list/kanban behaviour.
  const effectiveStatuses = effectiveTaskStatuses(status);

  // Build a cache key from the raw filter fields
  const filterKey = `${search}|${status.join(',')}|${priority.join(',')}|${assignedToId.join(',')}|${dateFrom?.getTime() ?? ''}|${dateTo?.getTime() ?? ''}|${location}`;

  if (mapTasks === cachedMapTasks && filterKey === cachedTaskFilterKey) {
    return cachedFilteredMapTasks;
  }

  cachedMapTasks = mapTasks;
  cachedTaskFilterKey = filterKey;

  // If no filters are active, still exclude archived tasks by default
  if (!selectHasActiveTaskFilters(state)) {
    cachedFilteredMapTasks = mapTasks.filter((task) =>
      effectiveStatuses.includes(task.status)
    );
    return cachedFilteredMapTasks;
  }

  const searchLower = search.toLowerCase();
  const dateFromStr = dateFrom ? dateToISOString(dateFrom) : null;
  const dateToStr = dateTo ? dateToISOString(dateTo) : null;
  const locationLower = location.toLowerCase();

  cachedFilteredMapTasks = mapTasks.filter((task) => {
    if (searchLower) {
      if (!task.title.toLowerCase().includes(searchLower)) return false;
    }

    // Status: always use effectiveStatuses (covers empty-selection → default-visible)
    if (!effectiveStatuses.includes(task.status)) {
      return false;
    }

    // Priority: task.priority must be in the priority array
    if (priority.length > 0 && !priority.includes(task.priority)) {
      return false;
    }

    // Assigned to: task.assignedToId must be in the assignedToId array
    if (
      assignedToId.length > 0 &&
      (task.assignedToId === null || !assignedToId.includes(task.assignedToId))
    ) {
      return false;
    }

    // Date range: compare day-level date strings against task.dueDate
    if (dateFromStr !== null || dateToStr !== null) {
      if (!task.dueDate) return false;
      const taskDateStr = dateToISOString(task.dueDate) ?? '';
      if (dateFromStr !== null && taskDateStr < dateFromStr) return false;
      if (dateToStr !== null && taskDateStr > dateToStr) return false;
    }

    // Location: case-insensitive includes
    if (locationLower) {
      const taskLocation = task.location?.toLowerCase() ?? '';
      if (!taskLocation.includes(locationLower)) return false;
    }

    return true;
  });

  return cachedFilteredMapTasks;
};
